import {
  HashRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import React, { Suspense } from "react";
import { Buffer } from "buffer";
import GoogleAnalyticsTracker from "./GoogleAnalyticsTracker";

import Login from "./pages/Login";

import "./App.css";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { LinearProgress } from "@mui/material";

import BaseLayout from "./layouts/BaseLayout";
import StudentWelcome from "./pages/student/StudentWelcome";
import TeacherWelcome from "./pages/teacher/TeacherWelcome";
import AdminWelcome from "./pages/admin/AdminWelcome";
import StudentScores from "./pages/student/StudentScores";
import ActivityHistory from "./pages/student/ActivityHistory";
import ActivitySearch from "./pages/student/ActivitySearch";
import PassHistory from "./pages/student/PassHistory";
import PassRequest from "./pages/student/PassRequest";
import Messages from "./pages/student/Messages";
import Signup from "./pages/student/Signup";
import Intake from "./pages/Intake";
import ViewAttendance from "./pages/teacher/ViewAttendance";
import TakeAttendance from "./pages/teacher/TakeAttendance";
import TeacherCalendar from "./pages/teacher/TeacherCalendar";
import { LicenseInfo } from "@mui/x-license";
import FindStudent from "./pages/teacher/FindStudent";
import ProtectedRoute from "./ProtectedRoute";
import TardyLogin from "./pages/TardyLogin";
import ProcessUserQr from "./pages/faculty/ProcessUserQr";
import ProcessUserQr2 from "./pages/faculty/ProcessUserQr2";
import VerifyOp from "./pages/pub/VerifyOp";
import VolunteerWelcome from "./pages/volunteer/VolunteerWelcome";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import PassKiosk from "./pages/faculty/PassKiosk";
import ActivityKiosk from "./pages/faculty/ActivityKiosk";
import QrScan from "./pages/QrScan";
import TardyKiosk from "./pages/faculty/TardyKiosk";


import About from "./pages/About";
import Suggest from "./pages/student/Suggest";
import ThisWeek from "./pages/teacher/ThisWeek";
import NextWeek from "./pages/teacher/NextWeek";
import AddScores from "./pages/teacher/AddScores";
import DailyRoster from "./pages/teacher/DailyRoster";
import ActivityDates from "./pages/teacher/ActivityDates";
import RepeatActivity from "./pages/teacher/RepeatActivity";
import ManageActivities from "./pages/teacher/ManageActivities";
import ManageActivity from "./pages/teacher/ManageActivity";
import ManageStudentLists from "./pages/teacher/ManageStudentLists";
import StudentListMembers from "./pages/teacher/StudentListMembers";
import Recommend from "./pages/teacher/Recommend";
import ScoreReceipt from "./pages/teacher/ScoreReceipt";
import BulkDelete from "./pages/teacher/BulkDelete";
import ManageScores from "./pages/teacher/ManageScores";
import ScoreReports from "./pages/teacher/ScoreReports";
import ScoreHistory from "./pages/teacher/ScoreHistory";
import BulkChange from "./pages/teacher/BulkChange";
import PassLeaveQr from "./pages/teacher/PassLeaveQr";
import ProcessPassQrLeave from "./pages/student/ProcessPassQrLeave";
import PassReturnQr from "./pages/teacher/PassReturnQr";
import ProcessPassQrReturn from "./pages/student/ProcessPassQrReturn";
import GenerateTardyQr from "./pages/teacher/GenerateTardyQr";
import UseTardyQr from "./pages/teacher/UseTardyQr";
import ActivitySignupQr from "./pages/teacher/ActivitySignupQr";
import UseActivitySignupQr from "./pages/teacher/UseActivitySignupQr";
import ActivityDateAttendQr from "./pages/teacher/ActivityDateAttendQr";
import UseActivityDateAttendQr from "./pages/teacher/UseActivityDateAttendQr";
import WhoCanViewActivity from "./pages/teacher/WhoCanViewActivity";
import Checkin from "./pages/teacher/Checkin";
import CheckinWelcome from "./pages/student/CheckinWelcome";
import StudentCheckins from "./pages/student/StudentCheckins";
import StudentComments from "./pages/student/StudentComments";
import CourseTakeAttendance from "./pages/teacher/CourseTakeAttendance";
import CourseThisWeek from "./pages/teacher/CourseThisWeek";
import ManageMeetings2 from "./pages/faculty/ManageMeetings2";
import TeacherSettings from "./pages/teacher/TeacherSettings";
import BulkChangeLocation from "./pages/teacher/BulkChangeLocation";
import ManageEvents from "./pages/faculty/ManageEvents";
import EventTakeAttendance from "./pages/faculty/EventTakeAttendance";
import EventSignupQr from "./pages/faculty/EventSignupQr";
import UseEventSignupQr from "./pages/teacher/UseEventSignupQr";
import ManageTeacherPasses from "./pages/faculty/ManageTeacherPasses";
import AddService from "./pages/student/AddService";
import StudentProfile from "./pages/faculty/StudentProfile";
import TardyLoginLocation from "./pages/TardyLoginLocation";
import UserListQr from "./pages/admin/UserListQr";
import UseEventGuardianQr from "./pages/guardian/UseEventGuardianQr";
import OpsByGrade from "./pages/volunteer/OpsByGrade";
import ConsequenceFulfillment from "./pages/faculty/ConsequenceFulfillment";

import ActivityAttendQr from "./pages/teacher/ActivityAttendQr";
import UseActivityAttendQr from "./pages/teacher/UseActivityAttendQr";

import HealthCheck from "./pages/admin/activity/HealthCheck";
import ActivitySignupAttendQr from "./pages/teacher/ActivitySignupAttendQr";
import UseActivitySignupAttendQr from "./pages/teacher/UseActivitySignupAttendQr";
import FacultyExcludeStudentsPass from "./pages/faculty/FacultyExcludeStudentsPass";
import ManageCache from "./pages/admin/cache/ManageCache";

/**
 * HOW TO
 */
const TeacherHowTos = React.lazy(() => import("./pages/TeacherHowTos"));

const StudentHowTos = React.lazy(() => import("./pages/StudentHowTos"));

const HowToTakeAttendance = React.lazy(
  () => import("./pages/how-to/teacher/HowToTakeAttendance"),
);

const HowToUseCalendar = React.lazy(
  () => import("./pages/how-to/teacher/HowToUseCalendar"),
);

const HowToCreateStudentList = React.lazy(
  () => import("./pages/how-to/teacher/HowtoCreateStudentList"),
);

const HowToMd = React.lazy(() => import("./pages/how-to/HowToMd"));

/**
 * REPORT ONLY
 */

const AvailableRooms = React.lazy(
  () => import("./pages/report/school/AvailableRooms"),
);

const Reports = React.lazy(() => import("./pages/faculty/Reports"));

const ReportWizard = React.lazy(() => import("./pages/faculty/ReportWizard"));

const Chat = React.lazy(() => import("./pages/Chat"));

const StudentCourses = React.lazy(
  () => import("./pages/student/StudentCourses"),
);

const StudentEvents = React.lazy(() => import("./pages/student/StudentEvents"));

const ManageStudentCalendar = React.lazy(
  () => import("./pages/faculty/ManageStudentCalendar"),
);

const ManageMeetings = React.lazy(
  () => import("./pages/faculty/ManageMeetings"),
);

const AttendanceStudent = React.lazy(
  () => import("./pages/report/student/AttendanceStudent"),
);

const SearchTeacherActivities = React.lazy(
  () => import("./pages/report/teacher/SearchTeacherActivities"),
);

const RoomSchedule = React.lazy(
  () => import("./pages/report/school/RoomSchedule"),
);

const EmptySeats = React.lazy(() => import("./pages/report/school/EmptySeats"));

const Departments = React.lazy(
  () => import("./pages/report/school/Departments"),
);

const Categories = React.lazy(() => import("./pages/report/school/Categories"));

const ActivitesByDate = React.lazy(
  () => import("./pages/report/activity/ActivitesByDate"),
);

const DefaultingForecast = React.lazy(
  () => import("./pages/report/activity/DefaultingForecast"),
);

const StudentsNoActivity = React.lazy(
  () => import("./pages/report/student/StudentsNoActivity"),
);

const StudentList = React.lazy(
  () => import("./pages/report/student/StudentList"),
);

const StudentActivities = React.lazy(
  () => import("./pages/report/student/StudentActivities"),
);

const WeeklyActivities = React.lazy(
  () => import("./pages/report/activity/WeeklyActivities"),
);

const PreviousAttendance = React.lazy(
  () => import("./pages/report/school/PreviousAttendance"),
);

const PassByStudent = React.lazy(
  () => import("./pages/report/pass/PassByStudent"),
);

const PassByLocation = React.lazy(
  () => import("./pages/report/pass/PassByLocation"),
);

const PassByTeacher = React.lazy(
  () => import("./pages/report/pass/PassByTeacher"),
);

const PassBasic = React.lazy(() => import("./pages/report/pass/PassBasic"));

const PassStudentCounts = React.lazy(
  () => import("./pages/report/pass/PassStudentCounts"),
);

const SchoolLocationCounts = React.lazy(
  () => import("./pages/report/school/SchoolLocationCounts"),
);

const DailyActivity = React.lazy(
  () => import("./pages/report/activity/DailyActivity"),
);

const ActivityMetrics = React.lazy(
  () => import("./pages/report/activity/ActivityMetrics"),
);

const AttendanceActivity = React.lazy(
  () => import("./pages/report/activity/AttendanceActivity"),
);

const TeacherActivities = React.lazy(
  () => import("./pages/report/teacher/TeacherActivities"),
);

const TeachersWithoutSignups = React.lazy(
  () => import("./pages/report/teacher/TeachersWithoutSignups"),
);

const EventsByDate = React.lazy(
  () => import("./pages/report/event/EventsByDate"),
);

const EventParents = React.lazy(
  () => import("./pages/report/event/EventParents"),
);

const ParentCounts = React.lazy(
  () => import("./pages/report/event/ParentCounts"),
);

/**
 * ADMIN ONLY
 */

const RapidAddStudents = React.lazy(
  () => import("./pages/admin/RapidAddStudents"),
);

const CommServiceTotals = React.lazy(
  () => import("./pages/admin/comm-service/CommServiceTotals"),
);

const StudentsWithoutGoals = React.lazy(
  () => import("./pages/admin/comm-service/StudentsWithoutGoals"),
);

const ActivityTags = React.lazy(
  () => import("./pages/report/activity/ActivityTags"),
);
const TeacherSignups = React.lazy(
  () => import("./pages/report/activity/TeacherSignups"),
);

const CommService = React.lazy(
  () => import("./pages/admin/comm-service/CommService"),
);

const CommServiceFiscalYears = React.lazy(
  () => import("./pages/admin/comm-service/CommServiceFiscalYears"),
);

const CommServiceStudent = React.lazy(
  () => import("./pages/student/CommServiceStudent"),
);

const CommServiceHistory = React.lazy(
  () => import("./pages/admin/comm-service/CommServiceHistory"),
);


const CommServiceByStatus = React.lazy(
  () => import("./pages/admin/comm-service/CommServiceByStatus"),
);

// this is needed for xlsx framework, as it is a workaround for it otherwise being used by Node.js
window.Buffer = Buffer;

const AttendanceSummary = React.lazy(
  () => import("./pages/report/activity/AttendanceSummary"),
);

const SchoolAbsentee = React.lazy(
  () => import("./pages/report/school/SchoolAbsentee"),
);

const SchoolLateness = React.lazy(
  () => import("./pages/report/school/SchoolLateness"),
);

const StudentByCategory = React.lazy(
  () => import("./pages/report/school/StudentByCategory"),
);

const MaxxedActivities = React.lazy(
  () => import("./pages/report/school/MaxxedActivities"),
);

const MostPopular = React.lazy(
  () => import("./pages/report/school/MostPopular"),
);

const RepeatStudents = React.lazy(
  () => import("./pages/report/school/RepeatStudents"),
);

const ActivityCategory = React.lazy(
  () => import("./pages/report/school/ActivityCategory"),
);

const LowAttendees = React.lazy(
  () => import("./pages/report/school/LowAttendees"),
);

const MeetingCodes = React.lazy(
  () => import("./pages/report/school/MeetingCodes"),
);

const DefaultedActivity = React.lazy(
  () => import("./pages/report/activity/DefaultedActivity"),
);

const DefaultedActivityStudent = React.lazy(
  () => import("./pages/report/activity/DefaultedActivityStudent"),
);

const ManageDepartments = React.lazy(
  () => import("./pages/admin/school/ManageDepartments"),
);

const ManagePeriodLabels = React.lazy(
  () => import("./pages/admin/school/ManagePeriodLabels"),
);

const ManageLocations = React.lazy(
  () => import("./pages/admin/school/ManageLocations"),
);

const ManageHomerooms = React.lazy(
  () => import("./pages/admin/school/ManageHomerooms"),
);

const ManageAdmins = React.lazy(
  () => import("./pages/admin/school/ManageAdmins"),
);

const ManageExcludeSync = React.lazy(
  () => import("./pages/admin/school/ManageExcludeSync"),
);

const ManageMeetingTypes = React.lazy(
  () => import("./pages/admin/meeting/ManageMeetingTypes"),
);

const ManageMeetingTopics = React.lazy(
  () => import("./pages/admin/meeting/ManageMeetingTopics"),
);

const ManageStudents = React.lazy(
  () => import("./pages/admin/school/ManageStudents"),
);

const ManageTeachers = React.lazy(
  () => import("./pages/admin/school/ManageTeachers"),
);

const EventSettings = React.lazy(() => import("./pages/admin/EventSettings"));

const ManageEventTypes = React.lazy(
  () => import("./pages/admin/event/ManageEventTypes"),
);

const ActivitySettings = React.lazy(
  () => import("./pages/admin/ActivitySettings"),
);

const SchoolSettings = React.lazy(() => import("./pages/admin/SchoolSettings"));

const ScoreSettings = React.lazy(() => import("./pages/admin/ScoreSettings"));

const PassSettings = React.lazy(() => import("./pages/admin/PassSettings"));

const MeetingSettings = React.lazy(
  () => import("./pages/admin/MeetingSettings"),
);

const StudentTransfer = React.lazy(
  () => import("./pages/report/student/StudentTransfer"),
);

const TopAttendance = React.lazy(
  () => import("./pages/report/student/TopAttendance"),
);

const TopDefaulted = React.lazy(
  () => import("./pages/report/student/TopDefaulted"),
);

const FindStudents = React.lazy(
  () => import("./pages/report/student/FindStudents"),
);

const TeacherLateCancel = React.lazy(
  () => import("./pages/report/teacher/TeacherLateCancel"),
);

const ProctorCounts = React.lazy(
  () => import("./pages/report/teacher/ProctorCounts"),
);

const StudentSignups = React.lazy(
  () => import("./pages/report/teacher/StudentSignups"),
);

const StudentsActivities = React.lazy(
  () => import("./pages/report/student/StudentsActivities"),
);

const AttendanceStudentRange = React.lazy(
  () => import("./pages/report/student/AttendanceStudentRange"),
);

const ActivityAbsenteePdf = React.lazy(
  () => import("./pages/report/school/ActivityAbsenteePdf"),
);

const TeachersWithoutAttendance = React.lazy(
  () => import("./pages/report/teacher/TeachersWithoutAttendance"),
);

const TeachersWithoutActivities = React.lazy(
  () => import("./pages/report/teacher/TeachersWithoutActivities"),
);

const ActivityAbsentee = React.lazy(
  () => import("./pages/report/school/ActivityAbsentee"),
);

const DailyTeacherRoster = React.lazy(
  () => import("./pages/report/teacher/DailyTeacherRoster"),
);

const LoginHistory = React.lazy(
  () => import("./pages/report/school/LoginHistory"),
);

const DailyActivityCount1 = React.lazy(
  () => import("./pages/report/school/DailyActivityCount1"),
);

const UserInactivity = React.lazy(
  () => import("./pages/report/teacher/UserInactivity"),
);

const ServicedStudents = React.lazy(
  () => import("./pages/report/teacher/ServicedStudents"),
);

const TopAbsentees = React.lazy(
  () => import("./pages/report/student/TopAbsentees"),
);

const AbsentStudent = React.lazy(
  () => import("./pages/report/student/AbsentStudent"),
);

const SeatTimeDeficitAll = React.lazy(
  () => import("./pages/report/score/SeatTimeDeficitAll"),
);

const CheckoutAll = React.lazy(
  () => import("./pages/report/score/CheckoutAll"),
);

const SeatTimeDeficit = React.lazy(
  () => import("./pages/report/score/SeatTimeDeficit"),
);

const TeacherScores = React.lazy(
  () => import("./pages/report/score/TeacherScores"),
);

const CheckinReport = React.lazy(
  () => import("./pages/report/score/CheckinReport"),
);

const AllCheckin = React.lazy(() => import("./pages/report/score/AllCheckin"));

const StudentsPresentDate = React.lazy(
  () => import("./pages/report/student/StudentsPresentDate"),
);

const StudentRosters = React.lazy(
  () => import("./pages/report/student/StudentRosters"),
);

const StudentsNoActivitySpecific = React.lazy(
  () => import("./pages/report/student/StudentsNoActivitySpecific"),
);

const UserListActivities = React.lazy(
  () => import("./pages/report/student/UserListActivities"),
);

const ConsequencesSchool = React.lazy(
  () => import("./pages/report/score/ConsequencesSchool"),
);

const ConsequencesStudent = React.lazy(
  () => import("./pages/report/score/ConsequencesStudent"),
);

const ConsequenceCounts = React.lazy(
  () => import("./pages/report/score/ConsequenceCounts"),
);

const SchoolComments = React.lazy(
  () => import("./pages/report/score/SchoolComments"),
);

const ScoreCounts = React.lazy(
  () => import("./pages/report/score/ScoreCounts"),
);

const ScoreDashboard = React.lazy(
  () => import("./pages/report/score/ScoreDashboard"),
);

const BulkAdds = React.lazy(() => import("./pages/report/activity/BulkAdds"));
const RestrictedActivities = React.lazy(
  () => import("./pages/admin/activity/RestrictedActivities"),
);
const ScoreFulfillments = React.lazy(
  () => import("./pages/report/score/ScoreFulfillments"),
);
const AttendanceSignupCounts = React.lazy(
  () => import("./pages/report/activity/AttendanceSignupCounts"),
);
const CloneDate = React.lazy(() => import("./pages/admin/activity/CloneDate"));
const LockedStudents = React.lazy(
  () => import("./pages/admin/activity/LockedStudents"),
);
const CommServiceSubmissionRanges = React.lazy(
  () => import("./pages/admin/comm-service/CommServiceSubmissionRanges"),
);
const StudentsWithoutTeachers = React.lazy(
  () => import("./pages/report/school/StudentsWithoutTeachers"),
);
const ManagePassTypeStudents = React.lazy(
  () => import("./pages/admin/pass/ManagePassTypeStudents"),
);
const ManageTeacherLocations = React.lazy(
  () => import("./pages/admin/pass/ManageTeacherLocations"),
);
const DataExport = React.lazy(() => import("./pages/admin/school/DataExport"));
const ManageSchoolSchedules = React.lazy(
  () => import("./pages/admin/school/ManageSchoolSchedules"),
);
const PassSchedule = React.lazy(
  () => import("./pages/admin/pass/PassSchedule"),
);
const ScoreCategoryLocationQr = React.lazy(
  () => import("./pages/admin/score/ScoreCategoryLocationQr"),
);
const ExcludeStudentsPass = React.lazy(
  () => import("./pages/admin/pass/ExcludeStudentsPass"),
);
const CommServiceRestrictedEmails = React.lazy(
  () => import("./pages/admin/comm-service/CommServiceRestrictedEmails"),
);
const CommServiceOrgs = React.lazy(
  () => import("./pages/admin/comm-service/CommServiceOrgs"),
);
const CommServiceReviewers = React.lazy(
  () => import("./pages/admin/comm-service/CommServiceReviewers"),
);
const CommServiceSettings = React.lazy(
  () => import("./pages/admin/comm-service/CommServiceSettings"),
);
const CheckinSettings = React.lazy(
  () => import("./pages/admin/CheckinSettings"),
);
const ManageSignupRules = React.lazy(
  () => import("./pages/admin/activity/ManageSignupRules"),
);
const UserQr = React.lazy(() => import("./pages/admin/UserQr"));
const ActivitySeatSuggestions = React.lazy(
  () => import("./pages/admin/activity/ActivitySeatSuggestions"),
);
const OverrideHours = React.lazy(
  () => import("./pages/admin/checkin/OverrideHours"),
);
const WeeklyHours = React.lazy(
  () => import("./pages/admin/checkin/WeeklyHours"),
);
const MonthlyHours = React.lazy(
  () => import("./pages/admin/checkin/MonthlyHours"),
);
const CommentHistory = React.lazy(
  () => import("./pages/admin/checkin/CommentHistory"),
);
const CheckinExcludedDates = React.lazy(
  () => import("./pages/admin/checkin/CheckinExcludedDates"),
);
const WeeklyMonthlyStudents = React.lazy(
  () => import("./pages/admin/checkin/WeeklyMonthlyStudents"),
);
const ManageRequests = React.lazy(
  () => import("./pages/admin/request/ManageRequests"),
);
const RepeatCourse = React.lazy(
  () => import("./pages/admin/course/RepeatCourse"),
);
const TeacherCourseCalendar = React.lazy(
  () => import("./pages/admin/course/TeacherCourseCalendar"),
);
const ManageCourseStudents = React.lazy(
  () => import("./pages/admin/course/ManageCourseStudents"),
);
const ManageTeacherCourses = React.lazy(
  () => import("./pages/admin/course/ManageTeacherCourses"),
);

const StudentBulkDelete = React.lazy(
  () => import("./pages/admin/activity/StudentBulkDelete"),
);

const TeacherActivityDates = React.lazy(
  () => import("./pages/admin/activity/TeacherActivityDates"),
);

const SpaceGapChange = React.lazy(
  () => import("./pages/admin/activity/SpaceGapChange"),
);

const AdminManageActivities = React.lazy(
  () => import("./pages/admin/activity/AdminManageActivities"),
);

const AdminCreateActivities = React.lazy(
  () => import("./pages/admin/activity/AdminCreateActivities"),
);
const TeacherBulkCalendarAdd = React.lazy(
  () => import("./pages/admin/TeacherBulkCalendarAdd"),
);
const ManageExcludedDates = React.lazy(
  () => import("./pages/admin/activity/ManageExcludedDates"),
);
const ActivitySignupWindows = React.lazy(
  () => import("./pages/admin/activity/ActivitySignupWindows"),
);

const ManageTeachersActivities = React.lazy(
  () => import("./pages/admin/activity/ManageTeachersActivities"),
);

const ManageCategories = React.lazy(
  () => import("./pages/admin/activity/ManageCategories"),
);

const ManageSuggestions = React.lazy(
  () => import("./pages/admin/activity/ManageSuggestions"),
);

const ManageTags = React.lazy(
  () => import("./pages/admin/activity/ManageTags"),
);

const ExcludeStudentsDate = React.lazy(
  () => import("./pages/admin/activity/ExcludeStudentsDate"),
);

const RoundRobinExcludeTeachers = React.lazy(
  () => import("./pages/admin/activity/RoundRobinExcludeTeachers"),
);

const ExcludeStudentsYear = React.lazy(
  () => import("./pages/admin/activity/ExcludeStudentsYear"),
);

const DefaultingHistory = React.lazy(
  () => import("./pages/admin/activity/DefaultingHistory"),
);

const DefaultActivitySettings = React.lazy(
  () => import("./pages/admin/activity/DefaultActivitySettings"),
);

const ManageStudentStudentBlocks = React.lazy(
  () => import("./pages/admin/school/ManageStudentStudentBlocks"),
);

const ManageStudentTeacherBlocks = React.lazy(
  () => import("./pages/admin/school/ManageStudentTeacherBlocks"),
);

const ManageSchoolAbsentees = React.lazy(
  () => import("./pages/admin/school/ManageSchoolAbsentees"),
);

const ManageGrades = React.lazy(
  () => import("./pages/admin/course/ManageGrades"),
);

const ManageSchoolSettings = React.lazy(
  () => import("./pages/admin/ManageSchoolSettings"),
);

const ManageAnnouncements = React.lazy(
  () => import("./pages/admin/school/ManageAnnouncements"),
);

const ManageConsequences = React.lazy(
  () => import("./pages/admin/score/ManageConsequences"),
);

const ManageCategoryConsequences = React.lazy(
  () => import("./pages/admin/score/ManageCategoryConsequences"),
);

const ManageScoreCategories = React.lazy(
  () => import("./pages/admin/score/ManageScoreCategories"),
);

const ManagePassNotifications = React.lazy(
  () => import("./pages/admin/pass/ManagePassNotifications"),
);

const ManagePasses = React.lazy(
  () => import("./pages/admin/pass/ManagePasses"),
);

const ManagePassTypes = React.lazy(
  () => import("./pages/admin/pass/ManagePassTypes"),
);

const ManageStudentGuardians = React.lazy(
  () => import("./pages/admin/school/ManageStudentGuardians"),
);

const Watchdog = React.lazy(() => import("./pages/report/school/Watchdog"));

const PassStudentClusters = React.lazy(
  () => import("./pages/report/pass/PassStudentClusters"),
);

const StudentTimeline = React.lazy(
  () => import("./pages/report/activity/StudentTimeline"),
);

const CheckinsNoCheckout = React.lazy(
  () => import("./pages/report/score/CheckinsNoCheckout"),
);

const NoCheckins = React.lazy(() => import("./pages/report/score/NoCheckins"));

const CheckinTimes = React.lazy(
  () => import("./pages/report/score/CheckinTimes"),
);

const ManageMetaPrompts = React.lazy(
  () => import("./pages/admin/school/ManageMetaPrompts"),
);

const ServiceStatus = React.lazy(() => import("./pages/ServiceStatus"));

const HomeroomReport = React.lazy(
  () => import("./pages/report/student/HomeroomReport"),
);

const AdminHomeroomReport = React.lazy(
  () => import("./pages/admin/activity/AdminHomeroomReport"),
);

const TeacherHomeroomReport = React.lazy(
  () => import("./pages/report/student/TeacherHomeroomReport"),
);

const EmailHistory = React.lazy(
  () => import("./pages/report/school/EmailHistory"),
);

const QrCodeGenerator = React.lazy(
  () => import("./pages/admin/activity/QrCodeGenerator"),
);

const SpotlightActivities = React.lazy(
  () => import("./pages/admin/activity/SpotlightActivities"),
);

const BulkAddScore = React.lazy(
  () => import("./pages/admin/score/BulkAddScore"),
);

const TeacherMap = React.lazy(
  () => import("./pages/teacher/TeacherMap"),
);

function isLikelyKnownRoute(pathname) {
  if (!pathname || pathname === "/") {
    return false;
  }

  if (pathname.startsWith("/#") || pathname.startsWith("/static")) {
    return false;
  }

  const prefixes = [
    "/adm",
    "/admin",
    "/teacher",
    "/student",
    "/faculty",
    "/volunteer",
    "/report",
    "/how-to",
    "/qr",
    "/user",
    "/activity",
    "/score",
    "/pass",
    "/comm-service",
    "/guardian",
    "/op",
    "/ap",
    "/intake",
    "/checkin",
    "/tlg",
    "/tlgl",
    "/tlg-student",
    "/about",
    "/chat",
    "/AiJMtdxNis04Lonfg9HdzOoVvIpJAu5b",
  ];

  return prefixes.some((prefix) => pathname.startsWith(prefix));
}

function RedirectUnknownPublicRoute() {
  const { pathname } = useLocation();

  if (pathname.startsWith("/adm") || pathname.startsWith("/admin")) {
    return null;
  }

  if (!isLikelyKnownRoute(pathname)) {
    return <Navigate to="/" replace />;
  }

  return null;
}

function RedirectUnknownAdminRoute() {
  const { pathname } = useLocation();

  if (pathname.startsWith("/adm") || pathname.startsWith("/admin")) {
    if (!isLikelyKnownRoute(pathname)) {
      return <Navigate to="/" replace />;
    }

    return null;
  }

  if (!isLikelyKnownRoute(pathname)) {
    return <Navigate to="/" replace />;
  }

  return null;
}

function App() {
  const STUDENT_ROLE = [500];
  const VOLUNTEER_ROLE = [525, 600, 700, 800];
  const TEACHER_ROLE = [600, 700, 800];
  const ADMIN_ROLE = [800];
  const FACULTY_ROLE = [600, 700, 800];

  LicenseInfo.setLicenseKey(
    "1bbded366ed438320693370440974af0Tz0xMjM2NjAsRT0xNzk4NTg4Nzk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI=",
  );

  const theme = createTheme({
    components: {
      MuiDialogTitle: {
        styleOverrides: {
          // Targets the root element of the DialogTitle
          root: {
            backgroundColor: "#f0f0f0", // Light gray background
            fontSize: "1.4em",
            fontWeight: 700,
            padding: "8px 16px",
            marginBottom: "8px",
            borderBottom: "1px solid #CCC",
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            backgroundColor: "#eee",
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            border: "1px solid #ccc",
            borderBottom: "none",
            marginLeft: "4px",
          },
        },
      },
    },
  });

  return (
    <BaseLayout>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <ThemeProvider theme={theme}>
          <HashRouter future={{ v7_startTransition: true }}>
            <GoogleAnalyticsTracker />
            <Routes>
              <Route path="/op/verify/:eid" element={<VerifyOp />} />
              <Route path="/ap/:fcmTokenHex" element={<Login />} />
              <Route path="/checkin" element={<Checkin />} />
              <Route path="/tlg" element={<TardyLogin />} />
              <Route path="/tlgl" element={<TardyLoginLocation />} />
              <Route
                path="/tlg-student"
                element={<TardyLogin isStudentLogin={true} />}
              />
              <Route path="/" element={<Login />} />
              <Route path="/intake/:schoolEid/:atJwt" element={<Intake />} />
              <Route
                path="/student/pass/qr/leave/:code"
                element={<ProcessPassQrLeave />}
              />
              <Route
                path="/student/pass/qr/return/:code"
                element={<ProcessPassQrReturn />}
              />
              <Route path="/student/tardy/qr/:code" element={<UseTardyQr />} />
              <Route
                path="/student/ad/signup/:code"
                element={<UseActivitySignupQr />}
              />
              <Route
                path="/student/ad/attend/:code"
                element={<UseActivityDateAttendQr />}
              />
              <Route
                path="/student/ad/signup-attend/:code"
                element={<UseActivitySignupAttendQr />}
              />
              <Route
                path="/student/activity/attend/:code"
                element={<UseActivityAttendQr />}
              />
              <Route
                path="/student/welcome"
                element={
                  <ProtectedRoute roles={STUDENT_ROLE}>
                    <StudentWelcome />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/checkin/welcome"
                element={
                  <ProtectedRoute roles={STUDENT_ROLE}>
                    <CheckinWelcome />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/checkin"
                element={
                  <ProtectedRoute roles={STUDENT_ROLE}>
                    <StudentCheckins />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/comments"
                element={
                  <ProtectedRoute roles={STUDENT_ROLE}>
                    <StudentComments />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/scores"
                element={
                  <ProtectedRoute roles={STUDENT_ROLE}>
                    <StudentScores />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/activity/history"
                element={
                  <ProtectedRoute roles={STUDENT_ROLE}>
                    <ActivityHistory />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/activity/search"
                element={
                  <ProtectedRoute roles={STUDENT_ROLE}>
                    <ActivitySearch />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/suggest"
                element={
                  <ProtectedRoute roles={STUDENT_ROLE}>
                    <Suggest />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/pass/history"
                element={
                  <ProtectedRoute roles={STUDENT_ROLE}>
                    <PassHistory />
                  </ProtectedRoute>
                }
              />
              <Route path="/student/pass/request" element={<PassRequest />} />
              <Route
                path="/student/messages"
                element={
                  <ProtectedRoute roles={STUDENT_ROLE}>
                    <Messages />
                  </ProtectedRoute>
                }
              />
              <Route path="/student/signup" element={<Signup />} />
              <Route
                path="/student/courses"
                element={
                  <ProtectedRoute roles={STUDENT_ROLE}>
                    <StudentCourses />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/events"
                element={
                  <ProtectedRoute roles={STUDENT_ROLE}>
                    <StudentEvents />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/find"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <FindStudent />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/activity/date"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <ActivityDates />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/activity/date/:teacherEid"
                element={
                  <ProtectedRoute roles={ADMIN_ROLE}>
                    <ActivityDates />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/activity/repeat"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <RepeatActivity />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/activity/repeat/:teacherEid"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <RepeatActivity />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/activity/location"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <BulkChangeLocation />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/welcome"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <TeacherWelcome />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/this-week"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <ThisWeek />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/next-week"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <NextWeek />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/score/add"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <AddScores />
                  </ProtectedRoute>
                }
              />
              <Route path="/score/receipt/:eids" element={<ScoreReceipt />} />
              <Route
                path="/teacher/ad/:activityDateEid/attendance/view"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <ViewAttendance />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/ad/:activityDateEid/attendance/take"
                element={
                  <ProtectedRoute roles={FACULTY_ROLE}>
                    <TakeAttendance />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/ad/:activityDateEid/attendance/kiosk"
                element={
                  <ProtectedRoute roles={FACULTY_ROLE}>
                    <ActivityKiosk />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/tardy/kiosk"
                element={
                  <ProtectedRoute roles={FACULTY_ROLE}>
                    <TardyKiosk />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/teacher/roster"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <DailyRoster />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/calendar"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <TeacherCalendar />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/:otherTeacherEid/calendar"
                element={
                  <ProtectedRoute roles={FACULTY_ROLE}>
                    <TeacherCalendar />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/:otherTeacherEid/map"
                element={
                  <ProtectedRoute roles={FACULTY_ROLE}>
                    <TeacherMap />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/activity/manage"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <ManageActivities />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/activity/:eid"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <ManageActivity />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/student-list"
                element={
                  <ProtectedRoute roles={FACULTY_ROLE}>
                    <ManageStudentLists />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/student-list/:eid"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <StudentListMembers />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/report/available-rooms"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <AvailableRooms />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/report/activity-metrics"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <ActivityMetrics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/report/daily-activity"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <DailyActivity />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/faculty/reports"
                element={
                  <Suspense
                    fallback={
                      <div style={{ padding: 16 }}>
                        <LinearProgress />
                      </div>
                    }
                  >
                    <ProtectedRoute roles={TEACHER_ROLE}>
                      <Reports />
                    </ProtectedRoute>
                  </Suspense>
                }
              />
              <Route
                path="/faculty/report-wizard"
                element={
                  <Suspense
                    fallback={
                      <div style={{ padding: 16 }}>
                        <LinearProgress />
                      </div>
                    }
                  >
                    <ProtectedRoute roles={TEACHER_ROLE}>
                      <ReportWizard />
                    </ProtectedRoute>
                  </Suspense>
                }
              />
              <Route
                path="/report/attendance-student"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <AttendanceStudent />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/report/search-teacher-activities"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <SearchTeacherActivities />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/report/room-schedule"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <RoomSchedule />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/report/empty-seats"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <EmptySeats />
                  </ProtectedRoute>
                }
              />
              <Route path="/about" element={<About />} />
              <Route
                path="/report/departments"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <Departments />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/report/categories"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <Categories />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/report/activities-by-date"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <ActivitesByDate />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/report/defaulting-forecast"
                element={
                  <ProtectedRoute roles={ADMIN_ROLE}>
                    <DefaultingForecast />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/report/activity-tags"
                element={
                  <ProtectedRoute roles={ADMIN_ROLE}>
                    <ActivityTags />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/report/students-no-activity"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <StudentsNoActivity />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/faculty/student-calendar/:eid"
                element={
                  <ProtectedRoute roles={FACULTY_ROLE}>
                    <ManageStudentCalendar />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/report/find-students"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <FindStudents />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/report/student-list-report"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <StudentList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/report/student-activities"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <StudentActivities />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/report/weekly-activities"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <WeeklyActivities />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/report/previous-attendance"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <PreviousAttendance />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/recommend"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <Recommend />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/report/pass-basic"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <PassBasic />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/report/pass-student-counts"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <PassStudentCounts />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/report/pass-by-student"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <PassByStudent />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/report/pass-by-location"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <PassByLocation />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/report/pass-by-teacher"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <PassByTeacher />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/bulk-delete"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <BulkDelete />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/manage-scores"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <ManageScores />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/report/scores"
                element={
                  <ProtectedRoute roles={FACULTY_ROLE}>
                    <ScoreReports />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/score/history"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <ScoreHistory />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/activity/bulk-change"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <BulkChange />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/pass/leave"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <PassLeaveQr />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/score/tardy"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <GenerateTardyQr />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/pass/return"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <PassReturnQr />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/pass/manage"
                element={
                  <ProtectedRoute roles={FACULTY_ROLE}>
                    <ManageTeacherPasses />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/ad/signup/:eid"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <ActivitySignupQr />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/ad/attend/:eid"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <ActivityDateAttendQr />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/ad/signup-attend/:eid"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <ActivitySignupAttendQr />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/activity/attend/:eid"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <ActivityAttendQr />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/activity/:eid/who-can-view"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <WhoCanViewActivity />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/course/:courseDateEid/attendance/take"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <CourseTakeAttendance />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/course/this-week"
                element={
                  <ProtectedRoute roles={FACULTY_ROLE}>
                    <CourseThisWeek />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/faculty/meetings"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <ManageMeetings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/faculty/meetings2"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <ManageMeetings2 />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/how-tos"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <TeacherHowTos />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/how-tos"
                element={
                  <ProtectedRoute roles={STUDENT_ROLE}>
                    <StudentHowTos />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/settings"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <TeacherSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/report/room-counts"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <SchoolLocationCounts />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/faculty/events"
                element={
                  <ProtectedRoute roles={FACULTY_ROLE}>
                    <ManageEvents />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/faculty/event/:eventEid/attendance/take"
                element={
                  <ProtectedRoute roles={FACULTY_ROLE}>
                    <EventTakeAttendance />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/report/attendance-activity"
                element={
                  <ProtectedRoute roles={FACULTY_ROLE}>
                    <AttendanceActivity />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/faculty/event/signup/:eid"
                element={
                  <ProtectedRoute roles={FACULTY_ROLE}>
                    <EventSignupQr />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/event/signup/qr/:code"
                element={<UseEventSignupQr />}
              />
              <Route
                path="/report/teacher-activities"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <TeacherActivities />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/report/teacher-0-signups"
                element={
                  <ProtectedRoute roles={TEACHER_ROLE}>
                    <TeachersWithoutSignups />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/chat"
                element={
                  <Suspense
                    fallback={
                      <div style={{ padding: 16 }}>
                        <LinearProgress />
                      </div>
                    }
                  >
                    <Chat />
                  </Suspense>
                }
              />

              <Route path="/user/idqr/:code" element={<ProcessUserQr />} />
              <Route
                path="/user/idqr/process/:code"
                element={
                  <ProtectedRoute roles={FACULTY_ROLE}>
                    <ProcessUserQr2 />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/adm/comm-service"
                element={
                  <ProtectedRoute roles={FACULTY_ROLE}>
                    <CommService />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/adm/comm-service/history"
                element={
                  <ProtectedRoute roles={VOLUNTEER_ROLE}>
                    <CommServiceHistory />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/adm/comm-service/totals"
                element={
                  <ProtectedRoute roles={VOLUNTEER_ROLE}>
                    <CommServiceTotals />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/student/service/add"
                element={
                  <ProtectedRoute roles={STUDENT_ROLE}>
                    <AddService />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/service/history"
                element={
                  <ProtectedRoute roles={STUDENT_ROLE}>
                    <CommServiceStudent />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/faculty/pass/excluded-students"
                element={
                  <ProtectedRoute roles={FACULTY_ROLE}>
                    <FacultyExcludeStudentsPass />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/profile/:eid"
                element={
                  <ProtectedRoute roles={FACULTY_ROLE}>
                    <StudentProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/volunteer/welcome"
                element={
                  <ProtectedRoute roles={VOLUNTEER_ROLE}>
                    <VolunteerWelcome />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/teacher/student-list/:eid/qr"
                element={
                  <ProtectedRoute roles={FACULTY_ROLE}>
                    <UserListQr />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/guardian/event/signup/qr/:eid"
                element={<UseEventGuardianQr />}
              />
              <Route
                path="/report/events-by-date"
                element={
                  <ProtectedRoute roles={FACULTY_ROLE}>
                    <EventsByDate />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/report/event-parents"
                element={
                  <ProtectedRoute roles={FACULTY_ROLE}>
                    <EventParents />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/report/parent-counts"
                element={
                  <ProtectedRoute roles={FACULTY_ROLE}>
                    <ParentCounts />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/volunteer/ops-by-grade"
                element={
                  <ProtectedRoute roles={VOLUNTEER_ROLE}>
                    <OpsByGrade />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/comm-service/students-no-goals"
                element={
                  <ProtectedRoute roles={VOLUNTEER_ROLE}>
                    <StudentsWithoutGoals />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/report/ops-by-status"
                element={
                  <ProtectedRoute roles={VOLUNTEER_ROLE}>
                    <CommServiceByStatus />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/faculty/score/fulfillment"
                element={
                  <ProtectedRoute roles={FACULTY_ROLE}>
                    <ConsequenceFulfillment />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/faculty/pass/kiosk"
                element={
                  <ProtectedRoute roles={FACULTY_ROLE}>
                    <PassKiosk />
                  </ProtectedRoute>
                }
              />

              <Route path="/admin/112233" element={<HealthCheck />} />
              <Route path="*" element={<RedirectUnknownPublicRoute />} />
            </Routes>

            {/*****************************************************************/}
            {/* ADMIN ROUTES   */}
            <Suspense
              fallback={
                <div style={{ padding: 16 }}>
                  <LinearProgress />
                </div>
              }
            >
              <Routes>
                <Route
                  path="/admin/welcome"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <AdminWelcome />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/activity"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ActivitySettings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/activity/rapid-add-students"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <RapidAddStudents />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/school"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <SchoolSettings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/score"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ScoreSettings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/pass"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <PassSettings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/meeting"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <MeetingSettings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/event"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <EventSettings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/event/type"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManageEventTypes />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/meeting/type"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManageMeetingTypes />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/meeting/topic"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManageMeetingTopics />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/student"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManageStudents />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/teacher"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManageTeachers />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/exclude-sync"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManageExcludeSync />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/admin"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManageAdmins />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/homeroom"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManageHomerooms />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/location"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManageLocations />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/period-label"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManagePeriodLabels />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/department"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManageDepartments />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/defaulted-activity"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <DefaultedActivity />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/defaulted-activity-student"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <DefaultedActivityStudent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/attendance-summary"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <AttendanceSummary />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/attendance-school-range"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <SchoolAbsentee />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/lateness-school-range"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <SchoolLateness />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/student-by-category"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <StudentByCategory />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/maxxed-activities"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <MaxxedActivities />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/most-popular-activities"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <MostPopular />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/repeat-students"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <RepeatStudents />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/activity-category-report"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ActivityCategory />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/low-attendees"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <LowAttendees />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/meeting-codes"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <MeetingCodes />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/login-history"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <LoginHistory />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/daily-activity-count"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <DailyActivityCount1 />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/user-inactivity"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <UserInactivity />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/serviced-students"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ServicedStudents />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/daily-teacher-roster"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <DailyTeacherRoster />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/absent-all"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ActivityAbsentee />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/absent-all/pdf"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ActivityAbsenteePdf />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/teacher-0-attendance"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <TeachersWithoutAttendance />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/teacher-0-activities"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <TeachersWithoutActivities />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/absent-all/pdf"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ActivityAbsenteePdf />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/teacher-late-cancel"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <TeacherLateCancel />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/proctor-counts"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ProctorCounts />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/student-signups"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <StudentSignups />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/students-activities"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <StudentsActivities />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/attendance-student-range"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <AttendanceStudentRange />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/where-am-i"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <FindStudent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/student-transfer"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <StudentTransfer />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/top-attendance"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <TopAttendance />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/top-defaulted"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <TopDefaulted />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/top-absentees"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <TopAbsentees />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/absent-student"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <AbsentStudent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/students-present-date"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <StudentsPresentDate />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/student-rosters"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <StudentRosters />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/students-no-activity-specific"
                  element={
                    <ProtectedRoute roles={TEACHER_ROLE}>
                      <StudentsNoActivitySpecific />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/user-list-activities"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <UserListActivities />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/consequences-school"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ConsequencesSchool />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/consequences-student"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ConsequencesStudent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/consequence-counts"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ConsequenceCounts />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/school-comments"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <SchoolComments />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/score-counts"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ScoreCounts />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/score-dashboard"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ScoreDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/teacher-scores"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <TeacherScores />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/checkin"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <CheckinReport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/all-checkin"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <AllCheckin />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/checkout-all"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <CheckoutAll />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/seat-time-deficit"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <SeatTimeDeficit />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/seat-time-deficit-all"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <SeatTimeDeficitAll />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/checkins-no-checkout"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <CheckinsNoCheckout />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/no-checkins"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <NoCheckins />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/checkin-times"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <CheckinTimes />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/pass-student-clusters"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <PassStudentClusters />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/student-timeline"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <StudentTimeline />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/student/:eid/guardian"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManageStudentGuardians />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/watchdog"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <Watchdog />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/manage-passes"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManagePasses />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/pass/type"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManagePassTypes />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/pass/teacher-locations"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManageTeacherLocations />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/pass/notify"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManagePassNotifications />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/score/category"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManageScoreCategories />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/score/consequence"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManageConsequences />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/score/category/:categoryId/consequences"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManageCategoryConsequences />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/announcement"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManageAnnouncements />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/school-settings"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManageSchoolSettings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/absentees"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManageSchoolAbsentees />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/grades"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManageGrades />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/student-teacher-blocks"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManageStudentTeacherBlocks />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/student-student-blocks"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManageStudentStudentBlocks />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/default-activity"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <DefaultActivitySettings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/default-history"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <DefaultingHistory />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/exclude/year"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ExcludeStudentsYear />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/exclude/rr/teacher"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <RoundRobinExcludeTeachers />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/exclude/date"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ExcludeStudentsDate />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/tags"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManageTags />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/suggestion"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManageSuggestions />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/activity/category"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManageCategories />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/activity/teachers"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManageTeachersActivities />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/school/excluded-date"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManageExcludedDates />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/school/activity-signup-windows"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ActivitySignupWindows />
                    </ProtectedRoute>
                  }
                />

                
                <Route
                  path="/adm/activity/teacher-bulk-change"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <AdminManageActivities />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/activity/teacher-bulk-add"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <AdminCreateActivities />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/activity/teacher-bulk-calendar-add"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <TeacherBulkCalendarAdd />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/activity/space-gap-change"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <SpaceGapChange />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/activity/teacher-activity-dates"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <TeacherActivityDates />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/activity/bulk-delete"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <StudentBulkDelete />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/course/teacher-courses"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManageTeacherCourses />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/course/:eid/students"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManageCourseStudents />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/course/calendar/:eid"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <TeacherCourseCalendar />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/teacher/course/repeat/:eid"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <RepeatCourse />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/request"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManageRequests />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/checkin/excluded-dates"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <CheckinExcludedDates />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/checkin/weekly-monthly-students"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <WeeklyMonthlyStudents />
                    </ProtectedRoute>
                  }
                />

                
                <Route
                  path="/adm/checkin/comment-history"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <CommentHistory />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/checkin/weekly-hours"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <WeeklyHours />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/checkin/monthly-hours"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <MonthlyHours />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/checkin/override-hours"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <OverrideHours />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/seat-suggestions/:date"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ActivitySeatSuggestions />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/user/idqr/:eid"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <UserQr />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/signup-rules"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManageSignupRules />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/checkin/settings"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <CheckinSettings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/comm-service/settings"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <CommServiceSettings />
                    </ProtectedRoute>
                  }
                />{" "}
                <Route
                  path="/adm/comm-service/reviewers"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <CommServiceReviewers />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/comm-service/orgs"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <CommServiceOrgs />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/comm-service/restricted-emails"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <CommServiceRestrictedEmails />
                    </ProtectedRoute>
                  }
                />{" "}
                <Route
                  path="/adm/pass/excluded-students"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ExcludeStudentsPass />
                    </ProtectedRoute>
                  }
                />{" "}
                <Route
                  path="/admin/score/locationQr/:eid"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ScoreCategoryLocationQr />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/pass/schedule"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <PassSchedule />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/schedules"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManageSchoolSchedules />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/data/export"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <DataExport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/pass/type/:eid/students"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManagePassTypeStudents />
                    </ProtectedRoute>
                  }
                />{" "}
                <Route
                  path="/report/students-without-teachers"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <StudentsWithoutTeachers />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/comm-service/submission-ranges"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <CommServiceSubmissionRanges />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/comm-service/fiscal-years"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <CommServiceFiscalYears />
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/adm/locked-students"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <LockedStudents />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/activity/clone-date"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <CloneDate />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/qr-code-generator"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <QrCodeGenerator />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/spotlight-activities"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <SpotlightActivities />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/bulk-add-score"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <BulkAddScore />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/attendance-signup-counts"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <AttendanceSignupCounts />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/score-fulfillments"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ScoreFulfillments />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/restricted-activities"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <RestrictedActivities />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/bulk-adds"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <BulkAdds />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/meta-prompts"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManageMetaPrompts />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adm/service-status"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ServiceStatus />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/homeroom-report"
                  element={
                    <ProtectedRoute roles={FACULTY_ROLE}>
                      <HomeroomReport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/teacher-homeroom-report"
                  element={
                    <ProtectedRoute roles={FACULTY_ROLE}>
                      <TeacherHomeroomReport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/admin-homeroom-report"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <AdminHomeroomReport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/activity/teacher-assigned"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <TeacherSignups />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/email-history"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <EmailHistory />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/how-to/take-attendance"
                  element={
                    <ProtectedRoute roles={FACULTY_ROLE}>
                      <HowToTakeAttendance />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/how-to/use-calendar-take-attendance"
                  element={
                    <ProtectedRoute roles={FACULTY_ROLE}>
                      <HowToUseCalendar />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/how-to/md"
                  element={
                    <ProtectedRoute roles={FACULTY_ROLE}>
                      <HowToMd />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/how-to/teacher/create-student-list"
                  element={
                    <ProtectedRoute roles={FACULTY_ROLE}>
                      <HowToCreateStudentList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/qr/scan"
                  element={
                    <ProtectedRoute roles={FACULTY_ROLE}>
                      <QrScan />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/AiJMtdxNis04Lonfg9HdzOoVvIpJAu5b"
                  element={
                    <ProtectedRoute roles={ADMIN_ROLE}>
                      <ManageCache />
                    </ProtectedRoute>
                  }
                />
                <Route path="*" element={<RedirectUnknownAdminRoute />} />
              </Routes>
            </Suspense>
          </HashRouter>
        </ThemeProvider>
      </LocalizationProvider>
    </BaseLayout>
  );
}

export default App;
